import { FC } from 'react';
import {
  Pwerm2CalculationWaterfallCaseYearMultipleDto,
  Pwerm2CalculationWaterfallCaseYearMultipleIrrBreakdownData,
} from '@app/shared/models/contracts/pwerm2-calculation-results-dto';
import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { useParams } from 'react-router-dom';
import CellValue from '@app/shared/components/cell-value/CellValue';
import {
  enteredPercentageViewRules,
  multiplierRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import { amountBasicFieldFormattingProps } from '@app/shared/components/form-controls/form-field/form-field-patterns';
import { Alignment } from '@app/shared/enums/alignment.enum';
import styles from '../sponsor-irr-and-hurdle.module.scss';
import SvgChevron from '@app/shared/icons/Chevron';
import classNames from 'classnames';
import MultipleCashFlow from './MultipleCashFlow';
import { getMultipleValue } from '@app/core/store/project-slice-selectors';
import { EventColumn } from './SponsorIRRTable';
import { getCalculatedProjectValueInPercent } from '@app/shared/formatters';
import { useFocusMode } from '@app/shared/hooks/focus-mode';
import { SponsorIrrFocusSelections } from '@app/shared/hooks/focus-mode/focus-mode-types';

interface MultipleRowProps {
  multipleId: string;
  multipleData: Pwerm2CalculationWaterfallCaseYearMultipleDto;
  eventColumns: EventColumn[];
  lastRowRef?: (el: HTMLTableRowElement | null) => void;
  forecastYear: string;
  onToggle: () => void;
  isExpanded: boolean;
}

const MultipleRow: FC<MultipleRowProps> = ({
  multipleId,
  multipleData,
  eventColumns,
  lastRowRef,
  forecastYear,
  onToggle,
  isExpanded,
}) => {
  const { caseId } = useParams<{ caseId: string }>();
  const project = useAppSelector((state) => state.project.projectDraft);
  const caseItem = project.pwermInput.cases.find((c) => c.caseId === caseId) ?? null;

  const multipleValue = caseItem ? getMultipleValue({ project, caseItem, multipleId }) : undefined;

  const { isActive, selections } = useFocusMode();
  const storedSelectedMultiple = (selections as SponsorIrrFocusSelections | undefined)?.multiple;
  const shouldGreyOut = isActive && storedSelectedMultiple && storedSelectedMultiple !== multipleId;

  const breakdownByOrderIndex = new Map<
    number,
    Pwerm2CalculationWaterfallCaseYearMultipleIrrBreakdownData
  >();
  Object.values(multipleData.institutionalIrrBreakdownData || {}).forEach((breakdown) => {
    breakdownByOrderIndex.set(breakdown.orderIndex, breakdown);
  });

  return (
    <>
      <tr ref={lastRowRef} className={shouldGreyOut ? styles['greyed-out'] : undefined}>
        {/* Header column */}
        <th className="table-primary__cell--vertical-separator" scope="row">
          <div className={styles['expand-toggle']}>
            <SvgChevron
              onClick={isActive && shouldGreyOut ? undefined : () => onToggle()}
              className={classNames(
                isExpanded ? styles['chevron-expanded'] : styles['chevron-collapsed']
              )}
            />
            <CellValue
              {...multiplierRules}
              value={multipleValue}
              className={classNames(styles['first-column-header'])}
            />
          </div>
        </th>
        {eventColumns.map((col) => {
          const breakdown = breakdownByOrderIndex.get(col.orderIndex);
          return breakdown ? (
            <td key={`${forecastYear}-${col.orderIndex}-${multipleId}`}>
              <CellValue
                value={breakdown.netEventAmount}
                {...amountBasicFieldFormattingProps}
                alignment={Alignment.Right}
              />
            </td>
          ) : (
            <td
              key={`empty-${forecastYear}-${col.orderIndex}-${multipleId}`}
              className={styles['empty-cell']}
            />
          );
        })}
        {/* IRR column */}
        <td>
          {multipleData.institutionalIrr === null ? (
            <></>
          ) : (
            <CellValue
              value={getCalculatedProjectValueInPercent(multipleData.institutionalIrr)}
              {...enteredPercentageViewRules}
            />
          )}
        </td>
      </tr>
      {isExpanded && (
        <MultipleCashFlow
          multipleId={multipleId}
          eventColumns={eventColumns}
          multipleData={multipleData}
        />
      )}
    </>
  );
};

export default MultipleRow;
